/**
 * THE GUARANTEED INCOME ON A VILLAGE PLAN (owner, 21 Oct 2026 — the guaranteed income review E4/E5/M4 and the
 * agreed mock-up, docs/ux-review/annuities/ screens 3, 4 and 6).
 *
 * One reading of a tested plan's guaranteed income, shared by the plan cards (RvPlansView), "Every way we
 * tested", the chosen-plan page (RvHub) and the advice document (rvSoa.ts):
 *   - what it is: the amount, the type (what the family receives) and whose it is;
 *   - the plan to compare it with: the same sale with the money invested instead (`rv-sell-home-invest`, or
 *     the same super top-up's twin), so the family table's last column is the guaranteed income alone;
 *   - whether choosing it is held: income only needs the client's dated confirmation for that amount.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { familyPanelKindOf, type FamilyPanelKind } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { incomeOnlyHeld } from '../../components/strategy/IncomeOnlyTick.tsx';
import { buildSoaGuaranteedIncome } from '../advice/soaGuaranteedIncome.ts';
import type { RvFamilyRecord } from './rvChosenPlan.ts';

interface RowLike {
    id?: string | null;
    annuityIncome?: number | null;
    projections?: unknown[] | null;
    inputs?: unknown;
    annuityTerms?: unknown;
    [key: string]: unknown;
}

export interface RvGuaranteedIncome {
    amount: number;
    kind: FamilyPanelKind;
    annuityType: string;
    residualPercent: number;
    /** Whose it is, as the screen and the document name them ("Kerry", or "Kerry and Joe" for a joint one). */
    personName: string;
    incomeAYear: number | null;
}

/** The guaranteed income a tested plan buys, from the engine's terms (or the plan's own inputs), or null. */
export function rvGuaranteedIncomeOf(row: RowLike | null | undefined, factFind: DraftFactFind | null | undefined): RvGuaranteedIncome | null {
    if (!row) return null;
    const terms = (row.annuityTerms || null) as { purchaseAmount?: unknown; residualPercent?: unknown; owner?: unknown; annuityType?: unknown } | null;
    const details = ((row.inputs as { sellHomeDetails?: Record<string, unknown> } | undefined)?.sellHomeDetails || {}) as Record<string, unknown>;
    const amount = Number(terms?.purchaseAmount ?? details.purchaseAnnuity) || 0;
    if (!(amount > 0)) return null;
    const annuityType = String(terms?.annuityType ?? details.rvAnnuityType ?? 'lifetime');
    if (annuityType === 'fixedTerm') return null;
    const residualPercent = Number(terms?.residualPercent ?? details.rvAnnuityResidualPercent) || 0;
    const owner = String(terms?.owner ?? details.rvAnnuityOwner ?? 'client');
    const client = factFind?.client?.firstName || 'you';
    const partner = factFind?.adviceFor === 'couple' ? (factFind?.partner?.firstName || 'your partner') : null;
    const personName = owner === 'partner' && partner ? partner : owner === 'joint' && partner ? `${client} and ${partner}` : client;
    const income = Number(row.annuityIncome);
    return {
        amount,
        kind: familyPanelKindOf({ annuityType, annuityResidualPercent: residualPercent }),
        annuityType,
        residualPercent,
        personName,
        incomeAYear: Number.isFinite(income) && income > 0 ? income : null,
    };
}

/**
 * The plan to compare with: the same sale, with the money invested instead of the guaranteed income. A debt
 * twin compares with the debt twin. Null where the field has no such plan.
 */
export function rvCompareFor<R extends RowLike>(row: R | null | undefined, strategies: readonly R[]): { row: R; note: string } | null {
    const id = String(row?.id || '');
    const debt = /-clear-debt$/.test(id) ? '-clear-debt' : '';
    const bare = id.replace(/-clear-debt$/, '');
    const pick = (target: string, note: string) => {
        const found = strategies.find((s) => String(s.id) === `${target}${debt}`);
        return found ? { row: found, note } : null;
    };
    if (/^rv-sell-home-annuity/.test(bare)) return pick('rv-sell-home-invest', 'the same sale, with the money invested instead');
    if (bare === 'rv-sell-home-downsizer-abp-annuity') return pick('rv-sell-home-downsizer-abp-invest', 'the same sale, with the money invested instead');
    if (bare === 'rv-sell-home-downsizer-annuity') return pick('rv-sell-home-downsizer', 'the same sale and super top-up, with the money left in the bank instead');
    return null;
}

/** The family table frozen for the saved plan (the plan page keeps no projection rows). */
export function rvFamilyRecordOf(
    gi: RvGuaranteedIncome | null,
    factFind: DraftFactFind | null | undefined,
    rows: readonly unknown[] | null | undefined,
    compare: { rows: readonly unknown[] | null | undefined; note: string } | null,
    horizonYears: number,
): RvFamilyRecord | null {
    if (!gi) return null;
    const built = buildSoaGuaranteedIncome({
        factFind, personName: gi.personName, amount: gi.amount, kind: gi.kind, incomeAYear: gi.incomeAYear,
        rows, compareRows: compare?.rows ?? null, compareNote: compare?.note ?? null, horizonYears,
        plan: { estate: 0, pension: 0, cashflow: 0 },
    });
    return built ? { personName: gi.personName, amount: gi.amount, kind: gi.kind, incomeAYear: gi.incomeAYear, compareNote: built.compareNote, rows: built.rows } : null;
}

/** Whether choosing this tested plan waits for the client's confirmation (income only, not yet confirmed for this amount). */
export function rvChoiceHeld(row: RowLike | null | undefined, factFind: DraftFactFind | null | undefined): boolean {
    const gi = rvGuaranteedIncomeOf(row, factFind);
    return !!gi && gi.kind === 'incomeOnly' && incomeOnlyHeld(factFind, true, gi.amount);
}
